import React, { useState, useEffect, useMemo } from 'react';
import MaterialTable from 'material-table';
import { IconButton, Tooltip, withStyles, Typography, Fab } from '@material-ui/core';
import { localization, getDimensionsOfPagination, hasPermission, hasAtLeastOnePermission } from '../../../sc-utilities';
import { Refresh, Add, Delete } from '@material-ui/icons';
import * as actions from './store/reportActions';
import * as action from '../../../np-core/Auth/store/authActions';
import { connect } from 'react-redux';
import Toolbar from '../../../sc-utilities/Toolbar'
import { CancelToken } from 'axios';
import { MuiTableRows } from '../../../../themes';
import NICDelete from '../../NIC/NICDelete';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem',
        },
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    fab: {
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const ReportList = props => {
    const { classes, pcProductGenerate, translations, onGenerate, saveColumnsFilter, mobileProductProductGenerate, storageProductProductGenerate, permissions, tableColumns, fetchReportCount, fetchReport, activeCustomerID, regenerateAction, productID, tableRef, isSingleTab, erasureReportType = 'pdf', onDeleteReports } = props;

    const [deleteReportsModalState, setDeleteReportsModalState] = useState({ open: false, ErasureReportIDs: [] });

    let columnObject = useMemo(() => { return tableColumns }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const erasureReportTypeTitleMapping = useMemo(() => {
        return {
            'pdf': translations["erasure_report"] || 'Erasure report',
            'verification-pdf': translations["verification_report"] || 'Erasure Verification report',
            'check-pdf': translations["asset_check_report"] || 'Asset check report'
        };
    }, [translations]);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ["system_erasure_report_delete"]),
        exportFileName: 'Report',
        filterCellStyle: { maxWidth: '200px' },
        search: false,
        minBodyHeight: window.innerHeight - 378 < 300 ? 300 : window.innerHeight - 378,
        maxBodyHeight: window.innerHeight - 378,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    useEffect(() => {
        if (regenerateAction > 0 || pcProductGenerate || mobileProductProductGenerate || storageProductProductGenerate) {
            tableRef.current && tableRef.current.onQueryChange();
        }
    }, [regenerateAction, pcProductGenerate, mobileProductProductGenerate, storageProductProductGenerate, tableRef])

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        q['ProductID'] = productID;
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        if (isSingleTab) {
            query.q.ErasureReportType = ['pdf', 'check-pdf'];
        } else {
            query.q.ErasureReportType = erasureReportType;
        }
        query['ProductID'] = productID
        let count = await fetchReportCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await fetchReport(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const handleGenerateReport = async () => {
        await onGenerate({ ActiveCustomerID: activeCustomerID, ProductID: productID, ErasureReportType: erasureReportType }, translations, erasureReportType);
        tableRef.current && tableRef.current.onQueryChange()
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Report`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const tableTitle = () => {
        return (
            <div className={classes.customToolbar}>
                <div className={classes.tableTitle}>
                    <Typography className={classes.typography}>{isSingleTab ? translations["reports"] || 'Reports' : erasureReportTypeTitleMapping[erasureReportType]} &nbsp;</Typography>
                    {hasPermission(permissions, "generate_new_report") ? (
                        <Fab
                            variant="extended"
                            size="small"
                            color="primary"
                            aria-label="Add"
                            style={{ borderRadius: '1px' }}
                            className={classes.fab}
                            onClick={handleGenerateReport}
                        >
                            <Add size="small" />
                            {translations["generate_new_report"] || "Generate new report"}
                        </Fab>
                    ) : null}
                    <Tooltip title={translations["refresh"] || "Refresh"}>
                        <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                            <Refresh id={'refresh_button'} />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
        )
    }

    const handleDeleteReports = ErasureReportIDs => {
        setDeleteReportsModalState({ open: true, ErasureReportIDs });
    };

    const handleDeleteReportsCloseSafe = () => {
        setDeleteReportsModalState({ open: false });
    };

    const handleDeleteReportsCloseDanger = async () => {
        await onDeleteReports({ ErasureReportIDs: deleteReportsModalState.ErasureReportIDs });
        setDeleteReportsModalState({ open: false });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableActions = [
        hasPermission(permissions, 'system_erasure_report_delete') ? {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["reports"] || "Reports"}`,
            icon: <Delete />,
            onClick: (rowData) => handleDeleteReports(rowData.map(erasureReport => erasureReport.ErasureReportID))
        } : {},
    ];

    const toolbar = {
        Toolbar: props => (
            <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
        )
    }

    return (
        <>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                components={toolbar}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                localization={localization(translations)}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />

            {hasPermission(permissions, "system_erasure_report_delete") ? (
                <NICDelete
                    activeCustomer={activeCustomerID}
                    entityName={translations["report"] || "Report"}
                    translations={translations}
                    handleCloseDanger={handleDeleteReportsCloseDanger}
                    handleDelete={handleDeleteReportsCloseSafe}
                    deleteModalState={deleteReportsModalState}
                />
            ) : null}
        </>
    )
}

const mapDispatchToProps = dispatch => {
    return {
        fetchReportCount: (filter, options) => dispatch(actions.fetchReportCount(filter, options)),
        fetchReport: (filter, options) => dispatch(actions.fetchReport(filter, options)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onDeleteReports: (data) => dispatch(actions.deleteReport(data)),
    };
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID || null,
        pcProductGenerate: state.product.pcProducts?.generate,
        mobileProductProductGenerate: state.product.mobileProductProducts?.generate,
        storageProductGenerate: state.product.storageProducts?.generate,
        permissions: state?.core?.login?.permissions || [],
        translations: state?.translate?.translations?.activeTranslations || {},
        columns: state?.core?.login?.userSettings?.Columns || null
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ReportList));